import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Bot } from '../lib/types';
import { ink, tint } from '../lib/tone';
import { Theme } from '@mui/material/styles';
import { WARN_TEXT, OVERLINE, RADIUS, TABLE_HEAD_SX, TONE } from '../lib/brand';
import { fmtIct } from '../lib/ict';
import { ProvLine } from '../chips';

/** "Derived" badge: the value was worked out, and the tooltip says exactly how. Nothing is guessed. */
export const DerivedBadge = ({ basis }: { basis?: string | null }) => (
  <Tooltip title={basis || 'No basis recorded'} arrow>
    <Box component="span" role="note" tabIndex={0} aria-label={`Derived. ${basis || 'No basis recorded'}`} sx={{ ml: 0.6, fontSize: 9.5, fontWeight: 700, px: 0.6, py: 0.1, borderRadius: RADIUS.sm, bgcolor: TONE.neutral.bg, color: TONE.neutral.fg, cursor: 'help', verticalAlign: 'middle' }}>DERIVED</Box>
  </Tooltip>
);

const STATUS_COLOR: Record<string, string | ((t: Theme) => string)> = {
  in_progress: ink(TONE.info.fg, TONE.info.border), done: ink(TONE.good.fg, TONE.good.border), blocked: WARN_TEXT, next: ink(TONE.neutral.fg, TONE.neutral.border),
};

export function WorkTab({ bot }: { bot: Bot }) {
  const tasks = bot.tasks || [];
  return (
    <Box>
      {tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="no-tasks">No tasks found in status/{bot.slug}.json.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Current work">
            <TableHead sx={TABLE_HEAD_SX}>
              <TableRow><TableCell>Task</TableCell><TableCell>Status</TableCell><TableCell>Started</TableCell><TableCell>Running for</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.title}>
                  <TableCell sx={{ maxWidth: 460 }}>
                    <Typography sx={{ fontSize: 13 }}>{t.title}</Typography>
                    <ProvLine prov={t.source} fmt={fmtIct} />
                  </TableCell>
                  <TableCell><Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: STATUS_COLOR[t.status] || 'text.secondary' }}>{(t.status || 'unknown').replace('_', ' ')}</Box></TableCell>
                  <TableCell>{t.started_at ? <>{fmtIct(t.started_at)}<DerivedBadge basis={t.started_basis} /></> : 'no data'}</TableCell>
                  <TableCell>{t.running_for ? <>{t.running_for}<DerivedBadge basis={t.running_for_basis} /></> : 'no data'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography sx={{ mt: 2, fontSize: 13 }}>
        <b>Last active</b> {fmtIct(bot.last_activity_ict)}
        {bot.activity && bot.activity.basis && <DerivedBadge basis={bot.activity.basis} />}
      </Typography>
      <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 1.5 }}>Blockers</Typography>
      {(bot.blockers || []).length === 0
        ? <Typography variant="body2" color="text.secondary">None stated in the bot&apos;s files.</Typography>
        : (bot.blockers || []).map((b) => (
          <Box key={b.text} sx={{ border: `1px solid ${TONE.bad.border}`, bgcolor: tint(TONE.bad.bg), borderRadius: RADIUS.lg, p: 1.2, mb: 0.8 }}>
            <Typography sx={{ fontSize: 13 }}>{b.text}</Typography>
            <ProvLine prov={b.prov} fmt={fmtIct} />
          </Box>
        ))}
    </Box>
  );
}
